<template>
  <div
    :class="`formulate-input-element formulate-input-element--${context.type}`"
    :data-type="context.type"
  >
    <div
      class="flex flex-row flex-wrap gap-2 w-full mt-6"
      :class="{ hidden: isCollapsed }"
    >
      <div
        v-for="option in context.options"
        :key="option.value"
        :data-is-selected="isSelected(option.value)"
        :title="option.description || option.label"
      >
        <button
          type="button"
          class="rounded-full w-auto text-sm relative outline-none focus:outline-none border-0 flex flex-grow-0 justify-start items-stretch"
          :class="{
            'opacity-40 cursor-not-allowed': isDisabled(option.value),
          }"
          @click="triggerSelected(option.value)"
        >
          <span
            :class="[
              'rounded-l-full py-1 px-2 text-xs',
              isDisabled(option.value)
                ? 'bg-truegray-300 text-gray-500 rounded-r-full'
                : isSelected(option.value)
                ? isContributor
                  ? 'bg-cw-alt text-white'
                  : 'bg-primary text-white'
                : 'bg-white text-gray-500 rounded-r-full border border-truegray-300',
            ]"
            >{{ getTagTitle(option) }}</span
          >
          <div
            v-if="isSelected(option.value) && !isDisabled(option.value)"
            :style="{ marginLeft: '1px' }"
            class="flex items-center justify-center rounded-r-full px-1"
            :class="[
              isDisabled(option.value)
                ? 'bg-truegray-300 text-gray-500'
                : isContributor
                ? 'bg-cw-alt text-white'
                : 'bg-primary text-white',
            ]"
          >
            <svg
              :style="{ right: '1px' }"
              xmlns="http://www.w3.org/2000/svg"
              class="h-3 w-3 relative"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
        </button>
      </div>
    </div>
    <FormulateInput
      v-if="showOtherTagInput"
      :name="otherTagInputName"
      label="Details (Other):"
      validation="required"
      validation-name="More detail"
      class="mt-4"
    />
  </div>
</template>

<script>
import { defineComponent, watch, ref, onMounted, computed } from 'vue'
import { useStore } from '../../../js/store/index.js'
import { EventBus } from '../../../js/plugins/event-bus.js'
import { EVENT_KEYS, ALL_REGIONS_TAG_ID } from '../../../js/util/constants.js'
import { getTagDisplayTitle } from '../../../js/util/helper.js'

export default defineComponent({
  name: 'VueFormulateTagsSelect',

  props: {
    context: {
      type: Object,
      required: true,
    },

    displayOnly: {
      type: Boolean,
      default: false,
    },

    isCollapsed: {
      type: Boolean,
      required: true,
    },

    isFilter: {
      type: Boolean,
      default: false,
    },

    connection: {
      type: Object,
      default: null,
    },
  },

  setup(props) {
    const store = useStore()
    const isContributor = computed(() => store.getters['shared/isContributor'])
    const selectedOptions = ref([])

    const ALL_REGIONS_TAG_ID_STRING = ALL_REGIONS_TAG_ID.toString()

    const isAllRegionsSelected = computed(() => {
      return isSelected(ALL_REGIONS_TAG_ID_STRING)
    })

    const otherTag = computed(() => {
      const options = props.context.options
      return options.find((tag) => tag.label === 'Other') || null
    })

    const otherTagInputName = computed(() => {
      if (otherTag.value) {
        const name = props.context.name
        return `${name}_other`
      }

      return null
    })

    const showOtherTagInput = computed(() => {
      return (
        !props.displayOnly &&
        isOtherTagSelected.value &&
        !props.isFilter &&
        !isContributor.value
      )
    })

    const isOtherTagSelected = computed(() => {
      const otherTagId = otherTag.value?.value || null
      return !!otherTagId && selectedOptions.value.includes(otherTagId)
    })

    const isDisabled = (value) => {
      return isAllRegionsSelected.value && value !== ALL_REGIONS_TAG_ID_STRING
    }

    const getTagTitle = (tagItem) => {
      if (props.displayOnly) {
        return getTagDisplayTitle(tagItem, props.connection.additional_info)
      }

      return tagItem.label
    }

    const isSelectedIndex = (value) => {
      return selectedOptions.value.indexOf(value)
    }

    const isSelected = (value) => {
      return isSelectedIndex(value) > -1
    }

    const triggerSelected = (value) => {
      const index = isSelectedIndex(value)
      if (index > -1) {
        // Unselect this option
        selectedOptions.value = [
          ...selectedOptions.value.slice(0, index),
          ...selectedOptions.value.slice(index + 1),
        ]
      } else {
        // If All Regions tag is selected or being selected,
        // don't allow selection of anything else!
        if (
          value === ALL_REGIONS_TAG_ID_STRING ||
          isSelected(ALL_REGIONS_TAG_ID_STRING)
        ) {
          selectedOptions.value = [ALL_REGIONS_TAG_ID_STRING]
          return
        }

        // Otherwise select this option too
        selectedOptions.value = [...selectedOptions.value, value]
      }
    }

    watch(selectedOptions, () => {
      props.context.model = selectedOptions.value
    })

    onMounted(() => {
      EventBus.$on(EVENT_KEYS.CO_VUE_FORMULATE_TAGS_UPDATED, (data) => {
        if (props.context.name === data.type) {
          selectedOptions.value = data.selectedOptions

          if (data.other) {
            data.form[otherTagInputName.value] = data.other
          }
        }
      })
    })

    return {
      ALL_REGIONS_TAG_ID_STRING,
      isContributor,
      selectedOptions,
      isAllRegionsSelected,
      triggerSelected,
      isSelected,
      isDisabled,
      isOtherTagSelected,
      otherTagInputName,
      showOtherTagInput,
      getTagTitle,
    }
  },
})
</script>
